/**
 * Størrelsesviser — tegner produktet i riktig skala mot et kjent referanseobjekt.
 *
 * Design handoff: theme/design_handoff_storrelsesviser/
 *
 * Alt regnes ut fra millimeter ganger en skalafaktor (pxmm), så proporsjonene er
 * ekte uansett skjermbredde. Elementet leser bredde og høyde fra data-attributter
 * satt av product-storrelsesviser.liquid.
 *
 * FAST SCENE
 * ----------
 * Skalaen regnes ut fra den STØRSTE referansen, ikke den valgte. Uten det ville
 * seksjonen hoppe i høyden hver gang kunden bytter chip, og produktrektangelet
 * ville endre størrelse — som er det motsatte av poenget. Produktet er like stort
 * i alle fire visningene; bare referansen bak det bytter.
 *
 * OVERLEGG VS. SIDE OM SIDE
 * -------------------------
 * Produktet legges oppå referansen når referansen er minst like stor i begge
 * akser. Ellers står de side om side med delt grunnlinje, slik designet viser
 * for mobil og bankkort. Regelen er bevisst ikke snudd andre veien: et bankkort
 * tegnet oppå notatboken blir tvetydig (ligger det foran eller bak?), mens delt
 * grunnlinje alltid leses riktig. Det gjelder også en kunstbok på 25 × 38 cm —
 * den står ved siden av A4-arket, ikke oppå det.
 */

if (!customElements.get('storrelsesviser-component')) {
  const REFERANSER = [
    { id: 'a4', label: 'A4-ark', w: 210, h: 297, note: 'A4-ark · 21 × 29,7 cm' },
    { id: 'pocket', label: 'Pocketbok', w: 110, h: 178, note: 'Pocketbok · 11 × 17,8 cm', fyll: '#e8ded4' },
    { id: 'phone', label: 'Mobil', w: 71.6, h: 147.6, note: 'Mobil · 7,2 × 14,8 cm' },
    { id: 'card', label: 'Bankkort', w: 85.6, h: 54, note: 'Bankkort · 8,6 × 5,4 cm' },
  ];

  const GAP_MM = 18;
  const MOBIL = 749; // temaets egen grense mellom mobil og desktop

  // Handoffens to sett med tall. Desktop først, mobil i parentes der de skiller lag.
  //
  // stageH er et TAK, ikke en målhøyde. Handoffens 460/330 er beholdt som
  // øvre grense, men den faktiske grensen regnes ut fra vindushøyden (se
  // takhoyde under) slik at hele modulen får plass uten å scrolle. På en lav
  // laptopskjerm er det tegningen som må gi etter, ikke kunden som må scrolle
  // for å se chipsene den skal trykke på.
  const MÅL = {
    desktop: { labelPx: 96, stageH: 460, minAvail: 220, minPxmm: 0.45, maksPxmm: 1.7, krom: 447, minStageH: 240 },
    mobil: { labelPx: 62, stageH: 330, minAvail: 180, minPxmm: 0.35, maksPxmm: 1.6, krom: 484, minStageH: 150 },
  };

  /**
   * Hvor høy får tegningen være for at alt annet også får plass i vinduet?
   *
   * `krom` er alt rundt selve stage-en — skuffens padding, overskrift, ingress,
   * produktnavn, mål, chip-raden, margene over og under tegningen, pluss de ca.
   * 40px skuffen selv holder av mot vindukanten. Det måles ikke, men er
   * kalibrert mot Dev 2: 405px krom på desktop, 438 på mobil, begge + 40.
   * Endres overskriftsstørrelse, ingress eller paddinger vesentlig, må tallene
   * justeres — ellers får man scroll tilbake på lave skjermer.
   */
  function takhoyde(m) {
    const plass = window.innerHeight - m.krom;
    return Math.max(m.minStageH, Math.min(m.stageH, plass));
  }

  /** 210 → «21», 297 → «29,7». Norsk desimalkomma, ingen etterslepende nuller. */
  function cm(mm) {
    return (mm / 10).toFixed(1).replace(/\.0$/, '').replace('.', ',');
  }

  class Storrelsesviser extends HTMLElement {
    connectedCallback() {
      this.bredde = parseFloat(this.dataset.bredde);
      this.hoyde = parseFloat(this.dataset.hoyde);
      if (!this.bredde || !this.hoyde) return;

      this.scene = this.querySelector('[data-scene]');
      this.stage = this.querySelector('[data-stage]');
      if (!this.scene || !this.stage) return;

      // Temaet flytter modalen til <body> når skuffen åpnes og tilbake når den
      // lukkes. Elementet kobles derfor fra og til på nytt for hver åpning, og
      // connectedCallback kjører flere ganger. Klikklytterne må bare bindes én
      // gang, ellers stables de opp for hver åpning.
      if (!this.bundet) {
        this.bundet = true;
        this.querySelectorAll('[data-ref]').forEach((knapp) => {
          knapp.addEventListener('click', () => this.velg(knapp.dataset.ref));
        });
      }

      // Nullstill via velg() og ikke ved å sette this.valgt direkte: chipsene er
      // DOM-tilstand og tegningen er JS-tilstand, og de må resettes sammen. Før
      // dette overlevde den aktive chipen en lukking mens tegningen falt tilbake
      // til A4, så knappen viste bankkort og tegningen A4.
      this.velg('a4');

      // Skuffen er skjult ved første måling, så scenen har bredde 0 til den
      // åpnes. ResizeObserver fanger åpningen uten at vi trenger å lytte på
      // selve modalen.
      if (typeof ResizeObserver !== 'undefined') {
        this.ro = new ResizeObserver(() => this.tegn());
        this.ro.observe(this.scene);
      }
      // Bundet én gang og tatt vare på, ellers kan den ikke fjernes igjen.
      // Resize må lyttes på fordi takhøyden avhenger av vindushøyden.
      this.paaResize = () => this.tegn();
      window.addEventListener('resize', this.paaResize);
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      if (this.paaResize) window.removeEventListener('resize', this.paaResize);
    }

    velg(id) {
      this.valgt = id;
      this.querySelectorAll('[data-ref]').forEach((knapp) => {
        const aktiv = knapp.dataset.ref === id;
        knapp.classList.toggle('storrelsesviser__chip--aktiv', aktiv);
        knapp.setAttribute('aria-pressed', aktiv ? 'true' : 'false');
      });
      this.tegn();
    }

    /**
     * Hvor stor plass trenger produkt + referanse sammen, i millimeter?
     * Overlegg bare når referansen rommer produktet.
     */
    utstrekning(ref) {
      const overlegg = ref.w >= this.bredde && ref.h >= this.hoyde;
      return {
        overlegg: overlegg,
        wmm: overlegg ? ref.w : ref.w + GAP_MM + this.bredde,
        hmm: Math.max(ref.h, this.hoyde),
      };
    }

    tegn() {
      const bredde = this.scene.clientWidth;
      if (!bredde) return; // skuffen er fortsatt lukket

      const m = window.innerWidth <= MOBIL ? MÅL.mobil : MÅL.desktop;
      const ref = REFERANSER.find((r) => r.id === this.valgt) || REFERANSER[0];

      // Fast scene: største utstrekning på tvers av alle fire referansene
      const alle = REFERANSER.map((r) => this.utstrekning(r));
      const maksW = Math.max(...alle.map((e) => e.wmm));
      const maksH = Math.max(...alle.map((e) => e.hmm));

      const tilgjengelig = Math.max(m.minAvail, bredde - m.labelPx);
      const pxmm = Math.max(
        m.minPxmm,
        Math.min(m.maksPxmm, Math.min(tilgjengelig / maksW, takhoyde(m) / maksH))
      );

      const e = this.utstrekning(ref);
      const rw = ref.w * pxmm;
      const rh = ref.h * pxmm;
      const pw = this.bredde * pxmm;
      const ph = this.hoyde * pxmm;

      this.stage.style.width = `${maksW * pxmm}px`;
      this.stage.style.height = `${maksH * pxmm}px`;
      this.stage.style.marginRight = `${m.labelPx}px`;

      // Referansen ligger alltid i nedre venstre hjørne. Produktet er det som
      // flytter seg — oppå ved overlegg, til høyre ellers. Delt hjørne er det
      // som gjør sammenligningen ærlig.
      const produktVenstre = e.overlegg ? 0 : rw + GAP_MM * pxmm;

      // `no-empty` er temaets egen unntaksklasse. Uten den treffes rektanglene
      // av `div:empty:not(...) { display: none }` i base-CSS-en, og tegningen
      // blir usynlig mens etikettene rundt står igjen — de har tekst i seg.
      //
      // Ved overlegg står referanseetiketten over arket. Ved side om side ville
      // den plasseringen svevd midt i tomrommet, så den flyttes ned under
      // referansen og brekker innenfor dens egen bredde.
      this.stage.innerHTML = `
        <div class="storrelsesviser__ref no-empty"
             style="width:${rw}px; height:${rh}px; background:${ref.fyll || '#ffffff'}"></div>
        <span class="storrelsesviser__ref-label" style="${
          e.overlegg
            ? `bottom:${rh + 8}px`
            : `bottom:-40px; white-space:normal; line-height:1.25; max-width:${Math.max(70, rw + GAP_MM * pxmm - 12)}px`
        }">${ref.note}</span>
        <div class="storrelsesviser__produkt no-empty"
             style="left:${produktVenstre}px; width:${pw}px; height:${ph}px"></div>
        <span class="storrelsesviser__mal" style="left:${produktVenstre}px; bottom:-22px">${cm(this.bredde)} cm</span>
        <span class="storrelsesviser__mal" style="left:${produktVenstre + pw + 10}px; bottom:${ph / 2 - 9}px">${cm(this.hoyde)} cm</span>
      `;
    }
  }

  customElements.define('storrelsesviser-component', Storrelsesviser);
}
